import ReactDOM from 'react-dom';
import { useIframeContext } from '../../context/IframeContext';
import { useEffect } from 'react';
import { ItemSummary, LayerItem } from '../../services/types';
import { capitalizeWords, formatValueBase, parseMarkdown } from '../../utils/helpers';

interface ItemModalProps {
    isOpen: boolean;
    onClose: () => void;
    item: LayerItem;
    summary: ItemSummary | null;
}

const ItemModal: React.FC<ItemModalProps> = ({ isOpen, onClose, item, summary }) => {
    const { isIframe } = useIframeContext();

    // Destructure metric properties
    const { description, name, value, category, activity_date, attributes, id, latitude, longitude } = item;

    // Format Activity Date
    const date = activity_date ? new Date(activity_date) : null;

    const formattedDate = date
        ? `${String(date.getUTCMonth() + 1).padStart(2, '0')}/${String(date.getUTCDate()).padStart(2, '0')}/${date.getUTCFullYear()}`
        : '';

    // Hide main scrollbar when the metadata panel is open
    useEffect(() => {
        if (isOpen) {
          document.body.style.overflow = 'hidden';
        } else {
          document.body.style.overflow = '';
        }

        return () => {
          document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;


    // Handle click event for sending point data
    const handleClick = () => {
        const origin = "*";
        // Send message to the parent window
        window.parent.postMessage(
            {
                message: "showProgram",
                data: {
                    id,
                    latitude,
                    longitude,
                },
            },
            origin
        );
        console.log("Program point message sent:", { id, latitude, longitude });
    };

    return ReactDOM.createPortal(
        <div className={`fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-35 transition-all duration-500 transform ${!isOpen ? 'translate-y-full' : 'translate-y-0'} ${!isIframe && 'max-w-[456px] mx-auto'}`}>
            <div className={`bg-white mx-4 w-full shadow-xl overflow-y-auto h-4/5 relative rounded-t-md  transition-transform duration-500 transform ${!isOpen ? 'translate-y-full' : 'translate-y-0'}`}>
                {/* Metric Name as Modal Header */}
                <div className="bg-port-green text-white text-sm w-full font-bold py-3 pl-6 flex">
                    {name}
                </div>
                <button
                    className="absolute top-1 right-5 text-white text-3xl hover:text-lightGray"
                    onClick={onClose}
                    aria-label="Close modal"
                >
                    &times;
                </button>
                {/* Show map location */}
                <div className='flex justify-end items-center px-6 my-4'>
                    <button onClick={handleClick} className='px-2 py-1 text-xs   bg-white hover:bg-color-cai-blue hover:text-white text-port-text border rounded-md shadow-lg'>
                            Show on Map
                    </button>
                </div>
                <div className='p-6 pt-0'>
                    {description && 
                        <p className='text-port-text text-xs w-11/12' dangerouslySetInnerHTML={{ __html: parseMarkdown(description) }}></p>
                    }
                    <div className="my-2">
                        {formattedDate &&
                        <>
                            <p className='text-port-text font-bold text-xs mt-3'>Date</p>
                            <p className='text-port-text text-xs w-11/12'>{formattedDate}</p>
                        </>
                        }
                        <div className='flex w-full mt-6'>
                            {category &&
                                <div className='flex flex-col w-full'>
                                    <p className='text-port-text font-bold text-xs'>Category</p>
                                    <p className='text-port-text text-xs'> {category}</p>
                                </div>
                            }
                            {value &&
                                <div className='flex flex-col w-full'>
                                    <p className='text-port-text font-bold text-xs'>Total</p>
                                    <p className='text-port-text text-xs'> {formatValueBase(value, 'currency')}</p>
                                    <p className='text-port-text text-[10px]'>{summary?.unit_descriptor}</p>
                                </div>
                            }
                        </div>
                    </div>

                    {/* Table Content */}
                    {attributes.length > 0 &&
                    <>
                        <h3 className="text-port-text text-xs font-bold">Key Attributes</h3>
                        <div className="space-y-4 text-port-text mt-2">
                            <div className="overflow-x-auto rounded-t-md">
                                <table className="min-w-full border-collapse border border-gray-300 border-t-port-green text-port-text">
                                    <thead>
                                        <tr className="bg-port-green">
                                            <th className="px-4 py-2 text-left text-xs text-white font-semibold">Field</th>
                                            <th className="px-4 py-2 text-left text-xs text-white font-semibold">Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* Attributes */}
                                        {attributes?.map((attr) => (
                                                <tr key={attr.name} className="hover:bg-gray-50">
                                                    <td className="border px-4 py-2 text-xs font-medium">{capitalizeWords(attr.name)}</td>
                                                    <td className="border px-4 py-2 text-xs" dangerouslySetInnerHTML={{ __html: parseMarkdown(attr.value) || "N/A" }}>
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                    }

                    
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ItemModal;